<template>
  <section class="stats-section">
    <div class="stats-container">
      <div class="section-header">
        <h2 class="section-title">
          <span class="section-title-icon">📊</span>
          <span class="section-title-text">{{ title }}</span>
        </h2>
        <p class="section-subtitle">{{ subtitle }}</p>
      </div>
      <div class="stats-grid" ref="statsGrid">
        <div
          v-for="stat in statistics"
          :key="stat.id"
          class="stat-card"
          @click="handleStatClick(stat)"
          :class="{ 'clickable': clickable }"
        >
          <div class="stat-number" :data-value="stat.value">{{ animatedValues[stat.id] || stat.value }}</div>
          <div class="stat-label">{{ stat.label }}</div>
          <div v-if="stat.trend" class="stat-trend" :class="`trend-${stat.trend.type}`">
            <span class="trend-icon">{{ stat.trend.type === 'up' ? '↑' : '↓' }}</span>
            <span class="trend-value">{{ stat.trend.value }}</span>
          </div>
        </div>
      </div>
      
      <!-- 可选的图表区域 -->
      <div v-if="showChart" class="chart-container">
        <slot name="chart"></slot>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'StatisticsSection',
  props: {
    title: {
      type: String,
      default: '学习统计数据'
    },
    subtitle: {
      type: String,
      default: '实时跟踪学习进度，智能分析学习效果'
    },
    statistics: {
      type: Array,
      default: () => [
        { id: 1, value: '25', label: '今日问答次数' },
        { id: 2, value: '8', label: '完成练习题数' },
        { id: 3, value: '3.5h', label: '今日学习时长' },
        { id: 4, value: '92%', label: '知识掌握度' },
        { id: 5, value: '15', label: '苏格拉底式引导' }
      ]
    },
    clickable: {
      type: Boolean,
      default: false
    },
    showChart: {
      type: Boolean,
      default: false
    },
    animateNumbers: {
      type: Boolean,
      default: true
    },
    backgroundColor: {
      type: String,
      default: 'white'
    }
  },
  data() {
    return {
      animatedValues: {},
      observer: null,
      hasAnimated: false
    }
  },
  mounted() {
    if (this.animateNumbers) {
      this.setupIntersectionObserver()
    }
    this.initAnimation()
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect()
    }
  },
  methods: {
    handleStatClick(stat) {
      if (this.clickable) {
        this.$emit('stat-click', stat)
      }
    },
    
    setupIntersectionObserver() {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting && !this.hasAnimated) {
              this.hasAnimated = true
              this.startNumberAnimation()
            }
          })
        },
        {
          threshold: 0.5
        }
      )
      
      if (this.$refs.statsGrid) {
        this.observer.observe(this.$refs.statsGrid)
      }
    },
    
    startNumberAnimation() {
      this.statistics.forEach(stat => {
        const finalValue = stat.value
        const isPercentage = finalValue.includes('%')
        const isTime = finalValue.includes('h')
        const numericValue = parseFloat(finalValue)
        
        if (!isNaN(numericValue)) {
          let current = 0
          const increment = numericValue / 50
          const suffix = isPercentage ? '%' : (isTime ? 'h' : '')
          
          const timer = setInterval(() => {
            current += increment
            if (current >= numericValue) {
              current = numericValue
              clearInterval(timer)
              this.$set(this.animatedValues, stat.id, finalValue)
            } else {
              const displayValue = isTime ? current.toFixed(1) : Math.floor(current)
              this.$set(this.animatedValues, stat.id, displayValue + suffix)
            }
          }, 30)
        } else {
          this.$set(this.animatedValues, stat.id, finalValue)
        }
      })
    },
    
    initAnimation() {
      // Card animation
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('animate-in')
            }
          })
        },
        {
          threshold: 0.1,
          rootMargin: '0px 0px -100px 0px'
        }
      )

      this.$nextTick(() => {
        const cards = this.$el.querySelectorAll('.stat-card')
        cards.forEach((card, index) => {
          card.style.opacity = '0'
          card.style.transform = 'translateY(40px)'
          card.style.transition = `opacity 0.6s ease ${index * 0.1}s, transform 0.6s ease ${index * 0.1}s`
          observer.observe(card)
        })
      })
    },
    
    // 公开方法：更新统计数据
    updateStat(statId, newValue) {
      const statIndex = this.statistics.findIndex(s => s.id === statId)
      if (statIndex !== -1) {
        this.$set(this.statistics[statIndex], 'value', newValue)
        if (this.animateNumbers) {
          // 重新动画新值
          this.animateSingleValue(this.statistics[statIndex])
        }
      }
    },
    
    animateSingleValue(stat) {
      const finalValue = stat.value
      const isPercentage = finalValue.includes('%')
      const isTime = finalValue.includes('h')
      const numericValue = parseFloat(finalValue)
      
      if (!isNaN(numericValue)) {
        const currentDisplay = this.animatedValues[stat.id] || '0'
        const currentValue = parseFloat(currentDisplay)
        const difference = numericValue - currentValue
        const increment = difference / 30
        const suffix = isPercentage ? '%' : (isTime ? 'h' : '')
        
        let steps = 0
        const timer = setInterval(() => {
          steps++
          const newValue = currentValue + (increment * steps)
          
          if (steps >= 30) {
            clearInterval(timer)
            this.$set(this.animatedValues, stat.id, finalValue)
          } else {
            const displayValue = isTime ? newValue.toFixed(1) : Math.floor(newValue)
            this.$set(this.animatedValues, stat.id, displayValue + suffix)
          }
        }, 20)
      }
    }
  },
  watch: {
    statistics: {
      deep: true,
      handler(newStats) {
        // 当统计数据变化时，更新动画值
        if (!this.animateNumbers) {
          newStats.forEach(stat => {
            this.$set(this.animatedValues, stat.id, stat.value)
          })
        }
      }
    }
  }
}
</script>

<style scoped>
/* CSS Variables */
.stats-section {
  --primary: #2E5CE6;
  --primary-dark: #1E3A8A;
  --primary-light: #60A5FA;
  --secondary: #10B981;
  --accent: #06B6D4;
  --asean: #D97706;
  --china-red: #DC2626;
  --success: #10B981;
  --warning: #F59E0B;
  --danger: #EF4444;
  --bg-white: #FFFFFF;
  --bg-gray-50: #F9FAFB;
  --bg-gray-100: #F3F4F6;
  --bg-gray-200: #E5E7EB;
  --text-primary: #111827;
  --text-secondary: #6B7280;
  --text-tertiary: #9CA3AF;
  --border-light: #E5E7EB;
  --border-medium: #D1D5DB;
  --shadow-xs: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  --shadow-sm: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  --radius-sm: 8px;
  --radius-md: 12px;
  --radius-lg: 16px;
  --radius-xl: 24px;
  --transition-base: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.stats-section {
  background: v-bind(backgroundColor);
  padding: 80px 0;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

.stats-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.section-header {
  margin-bottom: 48px;
  text-align: center;
}

.section-title {
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  letter-spacing: 0.5px;
}

.section-title-icon {
  font-size: 24px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
}

.section-title-text {
  line-height: 1.2;
  display: inline-flex;
  align-items: center;
}

.section-subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
  font-weight: 400;
  opacity: 0.8;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 24px;
  margin-top: 48px;
}

.stat-card {
  background: var(--bg-gray-50);
  border-radius: var(--radius-xl);
  padding: 32px;
  text-align: center;
  transition: var(--transition-base);
  border: 1px solid var(--border-light);
  position: relative;
  overflow: hidden;
}

.stat-card.clickable {
  cursor: pointer;
}

.stat-card::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--china-red) 0%, var(--primary) 50%, var(--asean) 100%);
  transform: scaleX(0);
  transition: var(--transition-base);
}

.stat-card:hover {
  transform: translateY(-4px);
  background: white;
  box-shadow: var(--shadow-xl);
}

.stat-card:hover::after {
  transform: scaleX(1);
}

.stat-card.animate-in {
  opacity: 1 !important;
  transform: translateY(0) !important;
}

.stat-number {
  font-size: 48px;
  font-weight: 800;
  background: linear-gradient(135deg, var(--china-red) 0%, var(--primary) 50%, var(--asean) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1;
  margin-bottom: 8px;
  min-height: 58px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-label {
  color: var(--text-secondary);
  font-size: 16px;
  font-weight: 500;
}

.stat-trend {
  margin-top: 12px;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.trend-up {
  color: var(--success);
}

.trend-down {
  color: var(--danger);
}

.trend-icon {
  font-size: 16px;
}

.chart-container {
  margin-top: 48px;
  background: var(--bg-gray-50);
  border-radius: var(--radius-xl);
  padding: 32px;
  box-shadow: var(--shadow-sm);
}

/* Responsive */
@media (max-width: 768px) {
  .stats-section {
    padding: 60px 0;
  }

  .stats-grid {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 16px;
  }

  .section-title {
    font-size: 28px;
  }

  .section-subtitle {
    font-size: 16px;
  }

  .stat-card {
    padding: 24px 16px;
  }

  .stat-number {
    font-size: 36px;
    min-height: 46px;
  }

  .stat-label {
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .stats-section {
    padding: 40px 0;
  }

  .section-title {
    font-size: 24px;
    flex-direction: column;
    gap: 8px;
  }

  .stats-grid {
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-top: 32px;
  }

  .stat-card {
    padding: 20px 12px;
  }

  .stat-number {
    font-size: 28px;
    min-height: 38px;
  }

  .stat-label {
    font-size: 12px;
  }
}

/* Loading state */
.stat-card.loading .stat-number {
  background: linear-gradient(90deg, var(--bg-gray-100) 25%, var(--bg-gray-200) 50%, var(--bg-gray-100) 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Animation performance optimization */
@media (prefers-reduced-motion: reduce) {
  .stat-card {
    transition: none !important;
  }
  
  .stat-number {
    transition: none !important;
  }
}
</style>